// RTK Query slice for the admin dashboard — list accounts, dashboard, and account management mutations.
// [context] Follows the same pattern as webhooks.slice.ts: createApi + fetchBaseQuery with Bearer token
// injection from Redux auth state, tag-based cache invalidation, and destructured hook exports.
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  ListAccountsResponse,
  ListAccountsParams,
  DashboardResponse,
  AdminAccountResponse,
  AdminUserMutationResponse,
  AdminUpdateEmailBody,
  ListAdminWebhooksParams,
  ListAdminWebhooksResponse,
  UpdateAdminWebhookBody,
  AdminWebhook,
  AdminWebhookWorkspace,
  CreateAdminWebhookBody,
  CreatedAdminWebhook,
} from './types';

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1',
    credentials: 'include',
    // [context] Attach Bearer token from Redux auth state so the API request is authenticated.
    prepareHeaders(headers, { getState }) {
      const token =
        (getState() as { auth: { accessToken: string | null } }).auth?.accessToken ?? null;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['AdminAccount', 'AdminWebhook'],
  endpoints: (builder) => ({
    // GET /api/v1/admin/accounts — paginated account list with search and status filter.
    listAccounts: builder.query<ListAccountsResponse, ListAccountsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.q) searchParams.set('q', params.q);
        if (params.status) searchParams.set('status', params.status);
        if (params.page) searchParams.set('page', String(params.page));
        if (params.perPage) searchParams.set('perPage', String(params.perPage));
        return `/admin/accounts?${searchParams.toString()}`;
      },
      providesTags: ['AdminAccount'],
    }),

    // GET /api/v1/admin/dashboard — admin-only dashboard data.
    getDashboard: builder.query<DashboardResponse, void>({
      query: () => '/admin/dashboard',
    }),

    // POST /api/v1/admin/accounts/:id/ban — ban a user account.
    banAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/ban`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/unban — unban a user account.
    unbanAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/unban`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/pause — pause a user account.
    pauseAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/pause`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/unpause — unpause a user account.
    unpauseAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/unpause`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/promote — promote a user to platform admin.
    promoteAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/promote`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/demote — demote a user from platform admin.
    demoteAccount: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/demote`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // PATCH /api/v1/admin/users/:id — admin user management (stub endpoint).
    updateUser: builder.mutation<
      AdminUserMutationResponse,
      { id: string; body: Record<string, unknown> }
    >({
      query: ({ id, body }) => ({ url: `/admin/users/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['AdminAccount'],
    }),

    // DELETE /api/v1/admin/users/:id — admin user deletion (stub endpoint).
    deleteUser: builder.mutation<AdminUserMutationResponse, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminAccount'],
    }),

    // PATCH /api/v1/admin/accounts/:id/email — admin changes a user's email and auto-verifies it.
    updateAccountEmail: builder.mutation<AdminAccountResponse, { id: string; body: AdminUpdateEmailBody }>({
      query: ({ id, body }) => ({ url: `/admin/accounts/${id}/email`, method: 'PATCH', body }),
      invalidatesTags: ['AdminAccount'],
    }),

    // POST /api/v1/admin/accounts/:id/verify-email — admin verifies a user's email address.
    verifyAccountEmail: builder.mutation<AdminAccountResponse, string>({
      query: (id) => ({ url: `/admin/accounts/${id}/verify-email`, method: 'POST' }),
      invalidatesTags: ['AdminAccount'],
    }),

    listAdminWebhooks: builder.query<ListAdminWebhooksResponse, ListAdminWebhooksParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params.q) searchParams.set('q', params.q);
        if (params.status) searchParams.set('status', params.status);
        if (params.scope) searchParams.set('scope', params.scope);
        if (params.page) searchParams.set('page', String(params.page));
        if (params.perPage) searchParams.set('perPage', String(params.perPage));
        return `/admin/webhooks?${searchParams.toString()}`;
      },
      providesTags: ['AdminWebhook'],
    }),

    listAdminWebhookEventTypes: builder.query<string[], void>({
      query: () => '/admin/webhooks/event-types',
      transformResponse: (response: { data: string[] }) => response.data,
    }),

    listAdminWebhookWorkspaces: builder.query<AdminWebhookWorkspace[], void>({
      query: () => '/admin/webhooks/workspaces',
      transformResponse: (response: { data: AdminWebhookWorkspace[] }) => response.data,
    }),

    createAdminWebhook: builder.mutation<{ data: CreatedAdminWebhook }, CreateAdminWebhookBody>({
      query: (body) => ({
        url: '/admin/webhooks',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['AdminWebhook'],
    }),

    updateAdminWebhook: builder.mutation<
      { data: AdminWebhook },
      { id: string; body: UpdateAdminWebhookBody }
    >({
      query: ({ id, body }) => ({
        url: `/admin/webhooks/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['AdminWebhook'],
    }),

    deleteAdminWebhook: builder.mutation<{ data: Record<string, never> }, string>({
      query: (id) => ({ url: `/admin/webhooks/${id}`, method: 'DELETE' }),
      invalidatesTags: ['AdminWebhook'],
    }),
  }),
});

export const {
  useListAccountsQuery,
  useGetDashboardQuery,
  useBanAccountMutation,
  useUnbanAccountMutation,
  usePauseAccountMutation,
  useUnpauseAccountMutation,
  usePromoteAccountMutation,
  useDemoteAccountMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useUpdateAccountEmailMutation,
  useVerifyAccountEmailMutation,
  useListAdminWebhooksQuery,
  useListAdminWebhookEventTypesQuery,
  useListAdminWebhookWorkspacesQuery,
  useCreateAdminWebhookMutation,
  useUpdateAdminWebhookMutation,
  useDeleteAdminWebhookMutation,
} = adminApi;
